import type { Locale } from "next-intl"
import { config } from "@/config"
import { env } from "@/env/client"

export function getUrlWithParams(pathname: string, params: URLSearchParams) {
  const paramsString = params.toString()

  if (!paramsString) return pathname

  return `${pathname}?${paramsString}`
}

export function getAbsoluteUrl(path?: string) {
  if (!path) return env.NEXT_PUBLIC_BASE_URL

  // Return early, if path already is an absolute URL (http://, https://, ftp://, mailto:, etc.)
  if (/^([a-z][a-z\d+.-]*):\/\/|^(mailto|tel|sms):/i.test(path)) return path

  return path.startsWith("/")
    ? `${env.NEXT_PUBLIC_BASE_URL}${path}`
    : `${env.NEXT_PUBLIC_BASE_URL}/${path}`
}

export function getLocalizedPath(locale: Locale, path?: string, alwaysAddLocale?: boolean) {
  const noPrefix =
    !alwaysAddLocale &&
    locale === config.i18n.defaultLocale &&
    config.i18n.localePrefixMode === "as-needed"

  const prefix = noPrefix ? "" : config.i18n.localePrefixes[locale]

  if (!path) return prefix

  return path.startsWith("/") ? `${prefix}${path}` : `${prefix}/${path}`
}

export function getAbsoluteLocalizedUrl(locale: Locale, path?: string, alwaysAddLocale?: boolean) {
  // Return early, if path already is an absolute URL (http://, https://, ftp://, mailto:, etc.)
  if (path && /^([a-z][a-z\d+.-]*):\/\/|^(mailto|tel|sms):/i.test(path)) return path

  const localizedPath = getLocalizedPath(locale, path, alwaysAddLocale)

  return `${env.NEXT_PUBLIC_BASE_URL}${localizedPath}`
}

export function getInngestDashboardUrl({
  organizationId,
  userId,
}: {
  organizationId?: string
  userId?: string
}) {
  // For local development, use the local Inngest dashboard
  if (env.NEXT_PUBLIC_DEVELOPMENT_MODE) {
    let url = "http://localhost:8288/runs"

    // Build search query in Inngest's expected format
    const searchParts: string[] = []
    if (userId) searchParts.push(`event.data.userId == '${userId}'`)
    if (organizationId) searchParts.push(`event.data.organizationId == '${organizationId}'`)

    if (searchParts.length > 0) {
      // Join with + for AND condition
      const searchQuery = searchParts.join(" + ")
      url += `?search=${encodeURIComponent(searchQuery)}`
    }

    return url
  }

  // For production, use the Inngest cloud dashboard URL
  // Determine environment based on VERCEL_ENV
  const vercelEnv = process.env.VERCEL_ENV
  let baseUrl: string

  if (vercelEnv === "preview") {
    // Preview environment
    baseUrl = "https://app.inngest.com/env/preview-fda9823e/runs"
  } else {
    // Production environment
    baseUrl = "https://app.inngest.com/env/production/runs"
  }

  // Build search query in Inngest's expected format
  const searchParts: string[] = []
  if (userId) searchParts.push(`event.data.userId == '${userId}'`)
  if (organizationId) searchParts.push(`event.data.organizationId == '${organizationId}'`)

  if (searchParts.length > 0) {
    // Join with + for AND condition
    const searchQuery = searchParts.join(" + ")
    baseUrl += `?search=${encodeURIComponent(searchQuery)}`
  }

  return baseUrl
}

// Keep the old function name as an alias for backward compatibility
// This can be removed once all references are updated
export const getTriggerDevRunsUrl = getInngestDashboardUrl

export function getPolarCustomerUrl({ customerId }: { customerId?: string | null }) {
  if (!customerId) return null

  if (env.NEXT_PUBLIC_PRODUCTION_MODE)
    return `https://polar.sh/dashboard/backlinkgpt/customers?customerId=${customerId}`

  return `https://sandbox.polar.sh/dashboard/backlinkgpt/customers?customerId=${customerId}`
}
